import { useEffect, useState } from "react";
import apiClient from "../../../shared/API/apiClient";
import Toast from "../../../shared/components/Toast";
import ReportingYearModal from "./ReportingYearModal";
import ReportingYearEditModal from "./ReportingYearEditModal";
import UnderConstructionModal from "./UnderConstructionModal";

const ManageReportingYear = () => {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addEnabled, setAddEnabled] = useState(false);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [editData, setEditData] = useState(null);         // triggers edit modal
  const [ucModal, setUcModal] = useState(null);           // "view" | "closure"
  const [cloningId, setCloningId] = useState(null);       // row id currently cloning

  const [closeRecord, setCloseRecord] = useState(null);

  const [toast, setToast] = useState(null);
  const showToast = (message, type = "success") => setToast({ message, type });

  // ── Fetch ─────────────────────────────────────────────────
  const fetchYears = async () => {
    try {
      setLoading(true);
      const res = await apiClient.post("/api/reportingyear");
      const data = res.data.data || [];
      setYears(data);

      // addentry: pick from any entry — if at least one says "enabled", enable the Add button
      const enabled = data.some((yr) => yr.addentry === "enabled");
      setAddEnabled(enabled);
    } catch (err) {
      console.error("Reporting year error", err);
      showToast(err.response?.data?.message || "Failed to load reporting years", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseReportingYear = async () => {
  try {
    const res = await apiClient.post("/api/reportingyear/close", {
      reporting_year_id: closeRecord.id,
    });

    showToast(
      res.data?.message || "Reporting Year closed successfully",
      "success"
    );

    setCloseRecord(null);
    fetchYears();

  } catch (err) {
    showToast(
      err.response?.data?.message || "Failed to close reporting year",
      "error"
    );
  }
};
  useEffect(() => { fetchYears(); }, []);

  // ── After add modal saves ─────────────────────────────────
  const handleAdded = (message) => {
    fetchYears();
    showToast(message || "Reporting year added successfully", "success");
    setShowAddModal(false);
  };

  // ── After edit modal saves ────────────────────────────────
  const handleEdited = (message) => {
    fetchYears();
    showToast(message || "Reporting year updated successfully", "success");
    setEditData(null);
  };

  // ── Clone ─────────────────────────────────────────────────
  const handleClone = async (yr) => {
    try {
      setCloningId(yr.id);
      const res = await apiClient.post("/api/reportingyear-clone", {
        reporting_year_id: yr.id,
        year_label: yr.year_label,
        start_date: yr.start_date,
        end_date: yr.end_date,
      });

      if (res.data?.code === 200 || res.data?.success) {
        showToast(res.data?.message || "Cloned successfully", "success");
        fetchYears();
      } else {
        throw new Error(res.data?.message || "Clone failed");
      }
    } catch (err) {
      console.error("Clone error:", err);
      showToast(err.response?.data?.message || err.message || "Clone failed", "error");
    } finally {
      setCloningId(null);
    }
  };

  // ── Helpers ───────────────────────────────────────────────
  const getStatus = (yr) => {
    if (yr.is_locked) return { label: "Closed", cls: "locked" };
    return { label: "Active", cls: "unlocked" };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="dash-page">

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Header */}
      <div className="le-page-header">
        <div>
          <div className="dash-page-title">Manage Reporting Year</div>
          <div className="dash-page-subtitle">Configure reporting years for BRSR reporting</div>
        </div>
       <button
            className={`org-btn-save le-add-btn ${!addEnabled ? "btn-disabled" : ""}`}
            onClick={() => setShowAddModal(true)}
            disabled={!addEnabled}
            title={!addEnabled ? "Only one active reporting year is allowed at a time." : ""}
            >
            + Add Reporting Year
            </button>
      </div>

      {/* List */}
      <div className="le-card">
        {loading ? (
          <div className="le-empty">
            <div className="le-empty-text">Loading reporting years...</div>
          </div>
        ) : years.length === 0 ? (
          <div className="le-empty">
            <div className="le-empty-text">No reporting years configured yet.</div>
            {addEnabled && (
              <button className="le-empty-btn" onClick={() => setShowAddModal(true)}>
                + Add your first reporting year
              </button>
            )}
          </div>
        ) : (
          <div className="table-wrap">
            <table className="le-table">
              <thead>
                <tr>
                  <th>Reporting Year</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {years.map((yr, i) => {
                  const status = getStatus(yr);
                  const isCloning = cloningId === yr.id;

                  return (
                    <tr key={yr.id}>
                    
                      <td data-label="Reporting Year">{yr.year_label}</td>
                      <td data-label="Start Date">{formatDate(yr.start_date)}</td>
                      <td data-label="End Date">{formatDate(yr.end_date)}</td>
                      <td data-label="Status">
                        <span className={`badge ${status.cls}`}>{status.label}</span>
                      </td>
                      <td data-label="Actions">
                        <div className="le-actions">

                          {yr.is_locked ? (
                            <>
                              

                              {/* Clone → API call */}
                              <span
                                
                              >
                                
                                N/A
                              </span>
                            </>
                          ) : (
                            <>
                              {/* Edit → Edit Modal */}
                              <span
                                className="action-item edit"
                                onClick={() => setEditData(yr)}
                              >
                                <img
                                  src="https://cdn-icons-png.flaticon.com/512/1159/1159633.png"
                                  alt="edit"
                                  className="action-icon"
                                />
                                Edit
                              </span>

                              {/* Request Closure → Under Construction */}
                              <span
  className="action-item edit"
  onClick={() => setCloseRecord(yr)}
>
  <img
    src="https://cdn-icons-png.flaticon.com/512/1828/1828843.png"
    alt="close"
    className="action-icon"
  />
  Close Reporting Year
</span>
                            </>
                          )}

                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <ReportingYearModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAdded}
        />
      )}

      {/* Edit Modal */}
      {editData && (
        <ReportingYearEditModal
          data={editData}
          onClose={() => setEditData(null)}
          onSave={handleEdited}
        />
      )}
      {closeRecord && (
  <div className="modal-overlay" onClick={() => setCloseRecord(null)}>
    <div
      className="modal-box modal-box--sm"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="modal-title">
        Close Reporting Year
      </div>
<center>
      <p className="delete-modal-text">
        Are you sure you want to close <strong>{closeRecord.year_label}</strong>?
        <br />
         This action cannot be undone.
       </p>
       </center>

      <div className="delete-modal-actions">
        <button
          className="org-btn-cancel"
          onClick={() => setCloseRecord(null)}
        >
          Cancel
        </button>

        <button
          className="delete-btn"
          onClick={handleCloseReportingYear}
        >
          Close Reporting Year
        </button>
      </div>
    </div>
  </div>
)}
      {/* Under Construction Modal */}
      {ucModal && (
        <UnderConstructionModal
          title={ucModal === "view" ? "View Reporting Year" : "Request Closure"}
          onClose={() => setUcModal(null)}
        />
      )}

    </div>
  );
};

export default ManageReportingYear;
